import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import { generationFailedMessage, getRefusalReason } from '@tunasong/redux'
import { getGenerationError } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import { useAlert } from '../alert/alert.hook.js'

/**
 * State for an AI style preview (an image or a music clip) that a request starts and a worker
 * fills in. A refusal is said out loud, whether the request was refused up front (e.g. out of AI
 * tokens) or the worker refused it later, instead of the spinner just stopping.
 *
 * @param what what is being made, for the alert: "image preview", "music preview"
 */
export const usePreviewGeneration = (what: string) => {
  const [entityId, setEntityId] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const { entity } = graphHooks.useEntity(entityId)
  const { alert } = useAlert()

  const reportFailure = (reason: string | undefined) =>
    alert({ severity: 'error', message: generationFailedMessage(what, reason) })

  // The worker has settled the stub: either the preview is ready, or it was refused
  useEffect(() => {
    if (!(pending && entity && !entity.pending)) {
      return
    }
    setPending(false)
    const generationError = getGenerationError(entity)
    if (generationError) {
      alert({ severity: 'error', message: generationFailedMessage(what, generationError.reason) })
    }
  }, [alert, entity, pending, what])

  const generate = async (request: () => Promise<{ id: string }>) => {
    setPending(true)
    try {
      const { id } = await request()
      setEntityId(id)
    } catch (error) {
      logger.warn(`Failed to generate the ${what}`, error)
      setPending(false)
      reportFailure(getRefusalReason(error))
    }
  }

  const reset = () => {
    setEntityId(null)
    setPending(false)
  }

  return { entity, pending, generate, reset }
}
